import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  Container,
  Typography,
  Card,
  Button,
  Modal,
  Box,
  IconButton,
  Divider,
  Chip,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Grid,
  CardContent,
  Paper,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import { createTheme, ThemeProvider, styled } from "@mui/material/styles";
// --- Importaciones para el gráfico NIVO ---
import { ResponsivePie } from "@nivo/pie";
// --- Importaciones para el PDF ---
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Certificate } from "./certificate"; // <-- Importa tu componente de certificado
import {
  Close as CloseIcon,
  Timer,
  CheckCircleOutline,
  HelpOutline,
  ArrowForwardIos,
  BarChart as BarChartIcon,
  GetApp as DownloadIcon,
  Replay as ReplayIcon,
  HighlightOff as IncorrectIcon,
  ListAlt as ListAltIcon,
  History as HistoryIcon,
  School,
  EmojiEvents,
  FactCheck,
  QueryStats,
  TrendingUp,
  PieChartOutline as PieChartOutlineIcon,
  DonutLarge as StatusIcon,
  CalendarToday as CalendarIcon,
  Repeat as AttemptsIcon,
  UpcomingOutlined as UpcomingIcon,
  ManageSearchOutlined as NoHistoryIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import HomeIcon from "@mui/icons-material/Home";
import axios from "axios";
// --- DATOS DE EJEMPLO (RESTAURADOS) ---
const availableExamsData = [
  {
    id: 1,
    title: "Examen de Álgebra Avanzada",
    description:
      "Una evaluación completa de tus conocimientos en álgebra, incluyendo ecuaciones, polinomios y matrices.",
    longDescription:
      "Este examen está diseñado para evaluar a fondo tu comprensión de los conceptos avanzados de álgebra. Cubre una amplia gama de temas, desde la resolución de ecuaciones lineales y cuadráticas hasta el trabajo con polinomios, funciones y teoría de matrices.",
    time: 90,
    questionsToPass: 15,
    difficulty: "Intermedio",
  },
  {
    id: 2,
    title: "Prueba de Cálculo Diferencial",
    description:
      "Mide tu dominio de los conceptos fundamentales del cálculo diferencial.",
    longDescription:
      "La prueba de cálculo diferencial se enfoca en los conceptos básicos de límites, derivadas y sus aplicaciones. Se evaluará tu capacidad para calcular derivadas de diversas funciones, interpretar su significado geométrico y resolver problemas de optimización.",
    time: 75,
    questionsToPass: 12,
    difficulty: "Básico",
  },
];

// const completedExamsData = [
//   {
//     id: 3,
//     title: "Evaluación de Geometría Analítica",
//     status: "Aprobado",
//     timeTaken: 45,
//     correctAnswers: 12,
//     incorrectAnswers: 3,
//     difficulty: "Intermedio",
//     dateTaken: "2025-10-15",
//     attempts: 1,
//   },
//   {
//     id: 4,
//     title: "Test de Lógica de Programación",
//     status: "Reprobado",
//     timeTaken: 30,
//     correctAnswers: 5,
//     incorrectAnswers: 10,
//     difficulty: "Experto",
//     dateTaken: "2025-10-28",
//     attempts: 2,
//   },
//   {
//     id: 5,
//     title: "Fundamentos de HTML",
//     status: "Aprobado",
//     timeTaken: 15,
//     correctAnswers: 10,
//     incorrectAnswers: 0,
//     difficulty: "Básico",
//     dateTaken: "2025-09-20",
//     attempts: 1,
//   },
// ];

// --- TEMA Y ESTILOS ---
/*const theme = createTheme({
  palette: {
    primary: { main: "#5E35B1" },
    secondary: { main: "#009688" }, // Teal
    success: { main: "#2e7d32" },
    error: { main: "#d32f2f" },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 600 },
    // h6 es más pequeño que h5
    h6: { fontWeight: 600 },
  },
});*/

const ModalBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 450, // Ancho para escritorio
  backgroundColor: theme.palette.background.alt, // Color de fondo modificado
  boxShadow: 24,
  padding: theme.spacing(4),
  borderRadius: "12px",
  outline: "none",
  [theme.breakpoints.down("sm")]: {
    width: "90%", // Ancho para móviles
    padding: theme.spacing(3),
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

// --- COMPONENTE PRINCIPAL ---
export default function TestUserDashboard() {
  const [tabIndex, setTabIndex] = useState(0);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [availableFilter, setAvailableFilter] = useState("Todos");
  const [completedFilter, setCompletedFilter] = useState("Todos");
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [availableSearch, setAvailableSearch] = useState("");
  const [completedSearch, setCompletedSearch] = useState("");
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const certificateRef = useRef();
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [loading, setLoading] = useState(true); // Para el indicador de carga
  const [error, setError] = useState(null); // Para manejar errores de la API
  const [allRows, setAllRows] = useState([]); // Almacenará los datos originales de la API
  const [items, setItems] = React.useState([]);
  const [datosUsuario, setDatosUsuario] = React.useState([]);
  const [completedExamsData, setCompletedExams] = useState([]);
  //Theme
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const nav = useNavigate();

  const obtenerIntentosDeExamen = async () => {
  setLoading(true);
  setError(null);
  try {
    // Reemplaza esta URL con tu endpoint de backend si es diferente
    // Asume que la URL base de axios ya está configurada a /api
    const response = await axios.get("/Examen");
    
    console.log("Datos de la API recibidos:", response.data);
    setAllRows(response.data); // Guardar datos originales

  } catch (err) {
    console.error("Error al obtener los intentos de examen:", err);
    const errorMessage = err.response?.data?.error || "No se pudieron cargar los datos. Intente de nuevo más tarde.";
    setError(errorMessage);
  } finally {
    setLoading(false);
  }
};
useEffect(() => {
    const items = JSON.parse(localStorage.getItem("token"));
    console.log(items,"pruebaassa")
    if (items) {
      setItems(items);
    }
   
  }, []);
useEffect(() => {
  obtenerIntentosDeExamen();
}, []); // El array vacío [] asegura que se ejecute solo una vez al inicio
  // --- CÁLCULO DE ESTADÍSTICAS ---
  const totalExamsTaken = completedExamsData.length;
  const certificationsAchieved = completedExamsData.filter(
    (exam) => exam.status === "Aprobado"
  ).length;
  const totalTime = completedExamsData.reduce(
    (acc, exam) => acc + exam.timeTaken,
    0
  );
  const averageTime =
    totalExamsTaken > 0 ? (totalTime / totalExamsTaken).toFixed(0) : 0;
  const successRate =
    totalExamsTaken > 0
      ? ((certificationsAchieved / totalExamsTaken) * 100).toFixed(0)
      : 0;

  const statsData = [
    {
      value: totalExamsTaken,
      label: "Exámenes Realizados",
      icon: <FactCheck />,
    },
    {
      value: certificationsAchieved,
      label: "Certificaciones",
      icon: <EmojiEvents />,
    },
    {
      value: `${averageTime} min`,
      label: "Tiempo Promedio",
      icon: <QueryStats />,
    },
    {
      value: `${successRate}%`,
      label: "Tasa de Aprobación",
      icon: <TrendingUp />,
    },
  ];

  // --- DATOS Y COLORES PARA EL GRÁFICO NIVO ---
  const nivoDifficultyData = [
    {
      id: "Básico",
      label: "Básico",
      value: completedExamsData.filter((e) => e.difficulty === "Básico").length,
    },
    {
      id: "Intermedio",
      label: "Intermedio",
      value: completedExamsData.filter((e) => e.difficulty === "Intermedio")
        .length,
    },
    {
      id: "Experto",
      label: "Experto",
      value: completedExamsData.filter((e) => e.difficulty === "Experto")
        .length,
    },
  ].filter((d) => d.value > 0);

  const colorMap = {
    Básico: "#2196F3", // Azul
    Intermedio: "#FFC107", // Amarillo
    Experto: "#F44336", // Rojo
  };

  // --- Función para búsqueda insensible a acentos ---
  const normalizeString = (str) =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  // --- Lógica de filtrado ---
  const filteredAvailableExams = allRows.filter((exam) => {
    const difficultyMatch =
      availableFilter === "Todos" || exam.dificultad === availableFilter;
    const searchMatch =
      availableSearch === "" ||
      normalizeString(exam.titulo_examen).includes(normalizeString(availableSearch));
    return difficultyMatch && searchMatch;
  });

  const filteredCompletedExams = completedExamsData.filter((exam) => {
    const difficultyMatch =
      completedFilter === "Todos" || exam.difficulty === completedFilter;
    const statusMatch =
      statusFilter === "Todos" || exam.status === statusFilter;
    const searchMatch =
      completedSearch === "" ||
      normalizeString(exam.title).includes(normalizeString(completedSearch));
    return difficultyMatch && statusMatch && searchMatch;
  });

  const handleTabChange = (event, newValue) => setTabIndex(newValue);
  const openDetailsModal = (exam) => {
    setSelectedExam(exam);
    setDetailsModalOpen(true);
  };
  const closeDetailsModal = () => setDetailsModalOpen(false);
  const openResultsModal = (exam) => {
    setSelectedExam(exam);
    setResultsModalOpen(true);
  };
  const closeResultsModal = () => setResultsModalOpen(false);

  const handleStartExam = (event,message,idExamen) => {
    console.log(`Iniciando examen: ${idExamen}`);
     nav(`/quiz?prop=${idExamen}`);
    closeDetailsModal();
    closeResultsModal();
  };

  const handleOpenCertificate = (examData) => {
    setSelectedExam(examData);
    console.log(examData);
    setResultsModalOpen(false);
    setCertificateModalOpen(true);
  };

  const handleCloseCertificate = () => {
    setCertificateModalOpen(false);
  };

  const handleDownloadCertificate = () => {
    if (!certificateRef.current) return;

    html2canvas(certificateRef.current, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "px",
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      pdf.save(`certificado-${selectedExam.title.replace(/\s/g, "_")}.pdf`);
    });
  };

  const handleDirectDownload = (examData) => {
    setSelectedExam(examData);
    setIsGeneratingPdf(true);
  };

  useEffect(() => {
    if (isGeneratingPdf && certificateRef.current) {
      html2canvas(certificateRef.current, {
        scale: 2,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "px",
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save(`certificado-${selectedExam.title.replace(/\s/g, "_")}.pdf`);
        setIsGeneratingPdf(false);
      });
    }
  }, [isGeneratingPdf, selectedExam]);

  const getDifficultyChipColor = (difficulty) => {
    if (difficulty === 1) return "success";
    if (difficulty === 2) return "warning";
    if (difficulty === 3) return "error";
    return "default";
  };

  //Breadcrumbs
  function handleClickBreadCrumbs(event) {
    event.preventDefault();
  }

  const handleLinkHome = (event, message) => {
    if (message === "home") nav("/inicio");
  };

  // Componente para cada celda de estadística
  const StatItem = ({ stat }) => (
    <Box
      sx={{
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
      }}
    >
      <Avatar
        sx={{
          bgcolor: "teal",
          color: "secondary.contrastText",
          width: 48,
          height: 48,
        }}
      >
        {React.cloneElement(stat.icon, { fontSize: "medium" })}
      </Avatar>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {stat.label}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          {stat.value}
        </Typography>
      </Box>
    </Box>
  );
/////////////// Historial de los examenes

  const datosPerfil = async () => {
    try {
       const { data1 } = await axios
              .get("/usaurioperfil/", {
                params: {
                  email: "jpirela@paluz.org",
                },
              })
              .then(function (response) {
                // setAlerta({})
      
               
                console.log("Aqui -->",response.data);
             setDatosUsuario(response.data)
                return;
              })
              .catch(function (error) {
                console.log("error");
                return;
              });
     } catch (error) {
      return;
    }
  };
useEffect(() => {
  const fetchHistory = async () => {
    try {
      
      const userId = items.id;

      const { data } = await axios.get(`/historial/${userId}`);
    
     datosPerfil();
     
      setCompletedExams(data); // ya viene en el formato que necesitas
    } catch (error) {
      console.error("Error cargando historial de exámenes:", error);
      // showSnackbar("Error cargando historial de exámenes", "error");
    }
  };

  fetchHistory();
}, [items]);
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ m: { xs: "1.5rem 1rem", sm: "1.5rem 2.5rem" } }}>
        <Box
          role="presentation"
          onClick={handleClickBreadCrumbs}
          sx={{ mb: 3 }}
        >
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              underline="hover"
              sx={{ display: "flex", alignItems: "center" }}
              color="inherit"
              href="/inicio"
              onClick={(event) => handleLinkHome(event, "home")}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Inicio
            </Link>
            <Typography
              sx={{ display: "flex", alignItems: "center" }}
              color="text.primary"
            >
              <School sx={{ mr: 0.5 }} fontSize="inherit" />
              Certificaciones
            </Typography>
          </Breadcrumbs>
          <Typography variant="h3" sx={{ fontWeight: "bold", mt: 2 }}>
            Certificaciones
          </Typography>
        </Box>
      </Box>
      <Container maxWidth={false} sx={{ px: { xs: 2, sm: 4 }, mt: 4, mb: 4 }}>
        <Grid container spacing={4}>
          {/* Columna de Estadísticas */}
          <Grid item xs={12} md={4}>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 3,
                    height: "100%",
                    bgcolor: "background.alt",
                  }}
                >
                  <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
                    <BarChartIcon color="primary" sx={{ mr: 1.5 }} />
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                      Tu Progreso
                    </Typography>
                  </Box>
                  <Divider sx={{ width: "99%", mx: "auto" }} />
                  <Grid container>
                    <Grid item xs={6}>
                      <StatItem stat={statsData[0]} />
                    </Grid>
                    <Grid item xs={6}>
                      <StatItem stat={statsData[1]} />
                    </Grid>
                  </Grid>
                  <Divider sx={{ width: "97%", mx: "auto" }} />
                  <Grid container>
                    <Grid item xs={6}>
                      <StatItem stat={statsData[2]} />
                    </Grid>
                    <Grid item xs={6}>
                      <StatItem stat={statsData[3]} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper
                  elevation={2}
                  sx={{ borderRadius: 3, p: 2, bgcolor: "background.alt" }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <PieChartOutlineIcon color="primary" sx={{ mr: 1.5 }} />
                    <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                      Dificultad de Exámenes Tomados
                    </Typography>
                  </Box>
                  <Divider sx={{ width: "99%", mx: "auto", my: 1.5 }} />
                  {nivoDifficultyData.length > 0 ? (
                    <Box sx={{ height: 250 }}>
                      <ResponsivePie
                        data={nivoDifficultyData}
                        margin={{ top: 20, right: 20, bottom: 60, left: 20 }}
                        innerRadius={0.5}
                        padAngle={2}
                        cornerRadius={3}
                        activeOuterRadiusOffset={8}
                        borderWidth={1}
                        borderColor={{
                          from: "color",
                          modifiers: [["darker", 0.2]],
                        }}
                        colors={(datum) => colorMap[datum.id]}
                        arcLinkLabelsSkipAngle={10}
                        arcLinkLabelsTextColor="#333333"
                        arcLinkLabelsThickness={2}
                        arcLinkLabelsColor={{ from: "color" }}
                        arcLabelsSkipAngle={10}
                        arcLabelsTextColor={{
                          from: "color",
                          modifiers: [["darker", 2]],
                        }}
                        legends={[
                          {
                            anchor: "bottom",
                            direction: "row",
                            justify: false,
                            translateX: 0,
                            translateY: 50,
                            itemsSpacing: 0,
                            itemWidth: 100,
                            itemHeight: 18,
                            itemTextColor: "#999",
                            itemDirection: "left-to-right",
                            itemOpacity: 1,
                            symbolSize: 18,
                            symbolShape: "circle",
                          },
                        ]}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        height: 250,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "text.secondary",
                      }}
                    >
                      <PieChartOutlineIcon sx={{ fontSize: 60 }} />
                      <Typography variant="h6" mt={1}>
                        No hay datos para mostrar
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>

          {/* Columna de Exámenes */}
          <Grid item xs={12} md={8}>
            <Card
              sx={{
                borderRadius: 4,
                boxShadow: 3,
                overflow: "hidden",
                bgcolor: "background.alt",
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={tabIndex}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  aria-label="pestañas de exámenes"
                  textColor="inherit"
                  sx={{
                    "& .Mui-selected": {
                      color: "grey.800",
                      fontWeight: "bold",
                    },
                    "& .MuiTabs-indicator": { backgroundColor: "grey.800" },
                  }}
                >
                  <Tab
                    icon={<ListAltIcon />}
                    iconPosition="start"
                    label="Disponibles"
                  />
                  <Tab
                    icon={<HistoryIcon />}
                    iconPosition="start"
                    label="Mi Historial"
                  />
                </Tabs>
              </Box>

              {/* Panel de Exámenes Disponibles */}
              <TabPanel value={tabIndex} index={0}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <TextField
                    label="Buscar Examen..."
                    variant="outlined"
                    size="small"
                    value={availableSearch}
                    onChange={(e) => setAvailableSearch(e.target.value)}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { sm: 200 },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { sm: 200 },
                    }}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel>Filtrar por Dificultad</InputLabel>
                      <Select
                        value={availableFilter}
                        label="Filtrar por Dificultad"
                        onChange={(e) => setAvailableFilter(e.target.value)}
                      >
                        <MenuItem value={"Todos"}>Todos</MenuItem>
                        <MenuItem value={1}>Básico</MenuItem>
                        <MenuItem value={2}>Intermedio</MenuItem>
                        <MenuItem value={3}>Experto</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                {filteredAvailableExams.length > 0 ? (
                  filteredAvailableExams.map((exam, index) => (
                    <React.Fragment key={exam.id}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 2,
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h6"
                            component="div"
                            sx={{ fontWeight: "bold" }}
                          >
                            {exam.titulo_examen}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="div"
                          >
                            <b>Dificultad:</b> {exam.dificultad == 1 ? "Basico": exam.dificultad == 2 ? "Intermedio":"Experto"}
                          </Typography>
                        </Box>
                        {isMobile ? (
                          <IconButton
                            onClick={() => openDetailsModal(exam)}
                            sx={{
                              ml: 1,
                              backgroundColor: "teal",
                              color: "secondary.contrastText",
                              "&:hover": {
                                backgroundColor: "secondary.dark",
                              },
                            }}
                          >
                            <ArrowForwardIos />
                          </IconButton>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => openDetailsModal(exam)}
                            sx={{
                              bgcolor: "teal",
                              whiteSpace: "nowrap",
                              ml: 2,
                            }}
                          >
                            Ver Detalles
                          </Button>
                        )}
                      </Box>
                      {index < filteredAvailableExams.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <Box sx={{ textAlign: "center", p: 5 }}>
                    <SearchIcon
                      sx={{ fontSize: 60, color: "text.secondary" }}
                    />
                    <Typography variant="h6" color="text.secondary" mt={1}>
                      No se encontraron resultados
                    </Typography>
                  </Box>
                )}
              </TabPanel>

              {/* Panel de Historial de Exámenes */}
              <TabPanel value={tabIndex} index={1}>
                {/* --- MODIFICACIÓN AQUÍ --- */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <TextField
                    label="Buscar Examen..."
                    variant="outlined"
                    size="small"
                    value={completedSearch}
                    onChange={(e) => setCompletedSearch(e.target.value)}
                    sx={{
                      width: { xs: "100%", sm: "auto" },
                      minWidth: { sm: 200 },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      width: { xs: "100%", sm: "40%" },
                    }}
                  >
                    <FormControl fullWidth size="small">
                      <InputLabel>Filtrar por Dificultad</InputLabel>
                      <Select
                        value={completedFilter}
                        label="Filtrar por Dificultad"
                        onChange={(e) => setCompletedFilter(e.target.value)}
                      >
                        <MenuItem value={"Todos"}>Todos</MenuItem>
                        <MenuItem value={"Básico"}>Básico</MenuItem>
                        <MenuItem value={"Intermedio"}>Intermedio</MenuItem>
                        <MenuItem value={"Experto"}>Experto</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small">
                      <InputLabel>Filtrar por Resultado</InputLabel>
                      <Select
                        value={statusFilter}
                        label="Filtrar por Resultado"
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <MenuItem value={"Todos"}>Todos</MenuItem>
                        <MenuItem value={"Aprobado"}>Aprobado</MenuItem>
                        <MenuItem value={"Reprobado"}>Reprobado</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                {filteredCompletedExams.length > 0 ? (
                  filteredCompletedExams.map((exam, index) => (
                    <React.Fragment key={exam.id}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          py: 2,
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="h6"
                              component="div"
                              sx={{ fontWeight: "bold" }}
                            >
                              {exam.title}
                            </Typography>
                            <Chip
                              label={exam.status}
                              color={
                                exam.status === "Aprobado" ? "success" : "error"
                              }
                              size="small"
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="div"
                          >
                            <b>Dificultad:</b> {exam.difficulty}
                          </Typography>
                        </Box>
                        {isMobile ? (
                          <IconButton
                            onClick={() => openResultsModal(exam)}
                            sx={{
                              ml: 1,
                              backgroundColor: "teal",
                              color: "secondary.contrastText",
                              "&:hover": {
                                backgroundColor: "secondary.dark",
                              },
                            }}
                          >
                            <ArrowForwardIos />
                          </IconButton>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => openResultsModal(exam)}
                            sx={{
                              bgcolor: "teal",
                              whiteSpace: "nowrap",
                              ml: 2,
                            }}
                          >
                            Ver Resultados
                          </Button>
                        )}
                      </Box>
                      {index < filteredCompletedExams.length - 1 && <Divider />}
                    </React.Fragment>
                  ))
                ) : (
                  <Box sx={{ textAlign: "center", p: 5 }}>
                    <SearchIcon
                      sx={{ fontSize: 60, color: "text.secondary" }}
                    />
                    <Typography variant="h6" color="text.secondary" mt={1}>
                      No se encontraron resultados
                    </Typography>
                  </Box>
                )}
              </TabPanel>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* --- MODALES --- */}
      {/* Modal de Detalles del Examen (para iniciar) */}
      <Modal open={detailsModalOpen} onClose={closeDetailsModal}>
        <ModalBox>
          <IconButton
            onClick={closeDetailsModal}
            sx={{
              bgcolor: "firebrick",
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              "&:hover": {
                backgroundColor: "darkred",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedExam && (
            <>
              <Typography
                variant="h4"
                component="h2"
                sx={{ fontWeight: "bold" }}
              >
                {selectedExam.title}
              </Typography>
              <Typography sx={{ mt: 2, color: "text.secondary" }}>
                {selectedExam.longDescription}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Timer sx={{ mr: 1.5, color: "primary.main" }} />
                  <Typography variant="body1">
                    <strong>Tiempo Límite:</strong> {selectedExam.tiempo_limite_minutos} minutos
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleOutline sx={{ mr: 1.5, color: "primary.main" }} />
                  <Typography variant="body1">
                    <strong>Para Aprobar:</strong>{" "}
                    {selectedExam.preguntas_para_aprobar} preguntas correctas
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <BarChartIcon sx={{ mr: 1.5, color: "primary.main" }} />
                  <Typography variant="body1" component="div">
                    <strong>Dificultad:</strong>{" "}
                    <Chip
                      label={selectedExam.dificultad ==1?"Basico":selectedExam.dificultad ==2?"Intermedio":"Experto"}
                      color={getDifficultyChipColor(selectedExam.dificultad)}
                      size="small"
                    />
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                fullWidth
                sx={{ bgcolor: "teal", mt: 3, fontWeight: "bold" }}
                onClick={(event) =>handleStartExam( event,
                                "Examen",
                                selectedExam.id_examen)}
             
                startIcon={<HelpOutline />}
              >
                Comenzar Examen
              </Button>
            </>
          )}
        </ModalBox>
      </Modal>
      {/* Modal de Resultados del Examen (Historial) */}
      <Modal open={resultsModalOpen} onClose={closeResultsModal}>
        <ModalBox>
          <IconButton
            onClick={closeResultsModal}
            sx={{
              bgcolor: "firebrick",
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
              "&:hover": {
                backgroundColor: "darkred",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedExam && (
            <>
              <Typography
                variant="h4"
                component="h2"
                mt={2}
                sx={{ fontWeight: "bold" }}
              >
                {selectedExam.title}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <StatusIcon
                    sx={{
                      mr: 1.5,
                      color:
                        selectedExam.status === "Aprobado"
                          ? "success.main"
                          : "error.main",
                    }}
                  />
                  <Typography variant="body1" component="div">
                    <strong>Resultado:</strong>{" "}
                    <Chip
                      label={selectedExam.status}
                      color={
                        selectedExam.status === "Aprobado" ? "success" : "error"
                      }
                      size="small"
                      sx={{ ml: 0.5 }}
                    />
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CheckCircleOutline sx={{ mr: 1.5, color: "success.main" }} />
                  <Typography variant="body1">
                    <strong>Correctas:</strong> {selectedExam.correctAnswers}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <IncorrectIcon sx={{ mr: 1.5, color: "error.main" }} />
                  <Typography variant="body1">
                    <strong>Incorrectas:</strong>{" "}
                    {selectedExam.incorrectAnswers}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Timer sx={{ mr: 1.5, color: "primary.main" }} />
                  <Typography variant="body1">
                    <strong>Tiempo:</strong> {selectedExam.timeTaken} minutos
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <CalendarIcon sx={{ mr: 1.5, color: "primary.main" }} />
                  <Typography variant="body1">
                    <strong>Fecha:</strong> {selectedExam.dateTaken}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <AttemptsIcon sx={{ mr: 1.5, color: "primary.main" }} />
                  <Typography variant="body1">
                    <strong>Intentos:</strong> {selectedExam.attempts}
                  </Typography>
                </Box>
              </Box>
              {selectedExam.status === "Aprobado" ? (
                isMobile ? (
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: "teal",
                      mt: 3,
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleDirectDownload(selectedExam)}
                    startIcon={<DownloadIcon />}
                  >
                    Descargar Certificado
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{
                      bgcolor: "teal",
                      mt: 3,
                      fontWeight: "bold",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => handleOpenCertificate(selectedExam)}
                    startIcon={<DownloadIcon />}
                  >
                    Ver Certificado
                  </Button>
                )
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 3,
                    bgcolor: "teal",
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                  onClick={handleStartExam}
                  startIcon={<ReplayIcon />}
                >
                  Volver a Intentar
                </Button>
              )}
            </>
          )}
        </ModalBox>
      </Modal>

      {/* --- MODAL DEL CERTIFICADO --- */}
      <Modal open={certificateModalOpen} onClose={handleCloseCertificate}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            outline: "none",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <IconButton
              onClick={handleCloseCertificate}
              sx={{
                bgcolor: "firebrick",
                position: "absolute",
                right: -5,
                top: -5,
                color: "white",
                zIndex: 10,
                "&:hover": {
                  backgroundColor: "darkred",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            {datosUsuario.length >0 ?  <Certificate
              ref={certificateRef}
              user={{ name: datosUsuario[0].nombres +" "+datosUsuario[0].apellidos, cedula: datosUsuario[0].cedula }} // Reemplaza con los datos reales del usuario
              exam={selectedExam}
            />: "asas"}
            
          </Box>
          <Button
            variant="contained"
            color="success"
            onClick={handleDownloadCertificate}
            sx={{ mt: 2, color: "white" }}
            fullWidth
            startIcon={<DownloadIcon />}
          >
            Descargar como PDF
          </Button>
        </Box>
      </Modal>

      {/* --- Contenedor oculto para la descarga directa en móvil --- */}
      {isGeneratingPdf && (
        <Box
          sx={{
            position: "absolute",
            left: "-9999px",
            top: 0,
            width: "842px",
            height: "595px",
          }}
        >
          <Certificate
            ref={certificateRef}
            user={{ name: "Nombre del Usuario", cedula: "V-00.000.000" }}
            exam={selectedExam}
            forceDesktop={true}
          />
        </Box>
      )}
    </ThemeProvider>
  );
}
